import React, { useContext } from 'react';
import TabLayout from './TabLayout';
import { useParams } from 'react-router';
import { Breadcrumb } from 'src/components/Breadcrumb';
import { CASE_DETAILS_PAGE_ROUTE } from 'src/constants/UrlConstants';
import { PageShellLayout, PageShell } from 'src/styles/pageLayoutStyles';
import { CaseSummaryPanel } from './CaseSummaryPanel';
import styled from 'styled-components';
import { CaseDetailsHydrater } from './providers/CaseDetailsHydrater';
import { CaseDetailsContext } from './providers/CaseDetailsProvider';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { fetchFromAppConfig } from 'src/config-store/ConfigFetcher';
import { CONFIG_TYPE } from 'src/constants/GlobalConstants';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { ReadMore } from 'src/components/ReadMore';

const CaseDetailsLayout = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const Header = styled.div`
  padding-bottom: 1rem;
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.xxl};
`;

const TitleLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xl};
  margin-right: 8px;
`;

//This is the base shell containing the page
export const ViewPageWithLeftPanelShell = styled(PageShell)`
  width: 70vw;
  min-width: 50rem;
`;

/**
 * This component decides whether to show/hide the Left panel.
 * @returns The View Page with the tab layout and the left panel if enabled.
 */
export const ViewPageDynamicLayout = () => {
  const { ddmAttributes, caseDetailsResponse } = useContext(CaseDetailsContext);
  const { preferredAccountId, clientId } = useContext(AuthContext);
  // Using react hook for displaying case title in document title
  useDocumentTitle(caseDetailsResponse?.caseTitle);
  const featureFlags = fetchFromAppConfig(
    preferredAccountId,
    clientId,
    CONFIG_TYPE.FEATURE_FLAGS,
  );
  if (ddmAttributes?.caseSummarySchema && featureFlags?.showLeftPanel) {
    return (
      <ViewPageWithLeftPanelShell>
        <CaseDetailsLayout>
          <CaseSummaryPanel />
          <TabContainer>
            <CaseTitle />
            <TabLayout />
          </TabContainer>
        </CaseDetailsLayout>
      </ViewPageWithLeftPanelShell>
    );
  }
  return (
    <PageShell>
      <CaseTitle />
      <TabLayout />
    </PageShell>
  );
};

export const CaseTitle = () => {
  const { caseDetailsResponse } = useContext(CaseDetailsContext);

  if (!caseDetailsResponse?.caseTitle) {
    return null;
  }

  return (
    <Header>
      <TitleLabel>Case Title: </TitleLabel>
      <ReadMore text={caseDetailsResponse?.caseTitle} maxlength={200} />
    </Header>
  );
};

const CaseDetails = () => {
  const { caseId } = useParams();
  return (
    <CaseDetailsHydrater caseId={caseId!}>
      <Breadcrumb page={CASE_DETAILS_PAGE_ROUTE} id={caseId}></Breadcrumb>
      <PageShellLayout>
        <ViewPageDynamicLayout />
      </PageShellLayout>
    </CaseDetailsHydrater>
  );
};

export default CaseDetails;
